import api from '../api';

export interface AIRiskSuggestion {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'CRITICAL';
  taxonomy: string[];
  confidence: number;
  reasoning: string;
}

export interface AIControlSuggestion {
  id: string;
  code: string;
  name: string;
  description: string;
  type: string;
  frequency: string;
  owner: string;
  effectiveness?: 'Effective' | 'Needs Review' | 'Ineffective';
  regulatory: {
    framework: string;
    reference: string;
    description: string;
  }[];
  confidence: number;
  reasoning: string;
}

export interface ProcessStepContext {
  id: number;
  name: string;
  description: string;
  type: string;
  industry?: string;
  regulatory?: string[];
  existingRisks?: any[];
  existingControls?: any[];
}

class AIRiskControlService {
  async suggestRisksForProcessStep(
    processStep: ProcessStepContext
  ): Promise<AIRiskSuggestion[]> {
    try {
      const response = await api.post('/api/ai/suggest-risks', {
        processStep,
        options: {
          includeRegulatory: true,
          includeIndustrySpecific: true,
          maxSuggestions: 5
        }
      });

      return response.data.risks.map((risk: any, index: number) => ({
        id: `ai-risk-${Date.now()}-${index}`,
        code: risk.code || `RISK-${processStep.id}-${index + 1}`,
        name: risk.name,
        description: risk.description,
        category: risk.category,
        riskLevel: risk.riskLevel || 'MEDIUM',
        taxonomy: risk.taxonomy || ['Operational Risk'],
        confidence: risk.confidence || 0.8,
        reasoning: risk.reasoning || 'AI-generated based on process step analysis'
      }));
    } catch (error) {
      console.error('Error suggesting risks:', error);
      
      // Fallback: Generate mock AI suggestions based on process step
      return this.generateMockRiskSuggestions(processStep);
    }
  }

  async suggestControlsForRisks(
    processStep: ProcessStepContext,
    risks: any[]
  ): Promise<AIControlSuggestion[]> {
    try {
      const response = await api.post('/api/ai/suggest-controls', {
        processStep,
        risks,
        options: {
          includeRegulatory: true,
          includeAutomation: true,
          maxSuggestions: 3
        }
      });

      return response.data.controls.map((control: any, index: number) => ({
        id: `ai-control-${Date.now()}-${index}`,
        code: control.code || `CTRL-${processStep.id}-${index + 1}`,
        name: control.name,
        description: control.description,
        type: control.type,
        frequency: control.frequency,
        owner: '',
        effectiveness: undefined,
        regulatory: control.regulatory || [],
        confidence: control.confidence || 0.8,
        reasoning: control.reasoning || 'AI-generated based on risk analysis'
      }));
    } catch (error) {
      console.error('Error suggesting controls:', error);
      
      // Fallback: Generate mock AI suggestions
      return this.generateMockControlSuggestions(processStep, risks);
    }
  }

  async enhanceExistingRisk(
    risk: any,
    processStep: ProcessStepContext
  ): Promise<Partial<AIRiskSuggestion>> {
    try {
      const response = await api.post('/api/ai/enhance-risk', {
        risk,
        processStep
      });

      return response.data.enhancements;
    } catch (error) {
      console.error('Error enhancing risk:', error);
      return {};
    }
  }

  async enhanceExistingControl(
    control: any,
    risks: any[],
    processStep: ProcessStepContext
  ): Promise<Partial<AIControlSuggestion>> {
    try {
      const response = await api.post('/api/ai/enhance-control', {
        control,
        risks,
        processStep
      });

      return response.data.enhancements;
    } catch (error) {
      console.error('Error enhancing control:', error);
      return {};
    }
  }

  // Mock data generators for fallback
  private generateMockRiskSuggestions(processStep: ProcessStepContext): AIRiskSuggestion[] {
    const baseRisks = [
      {
        name: 'Identity Verification Failure',
        description: 'Risk of inadequate customer identity verification leading to compliance violations',
        category: 'Compliance & Legal Risk',
        riskLevel: 'HIGH' as const,
        taxonomy: ['Operational Risk', 'Compliance & Legal Risk', 'AML/KYC Risk']
      },
      {
        name: 'Data Privacy Breach',
        description: 'Risk of unauthorized access or exposure of customer personal information',
        category: 'Information Security Risk',
        riskLevel: 'HIGH' as const,
        taxonomy: ['Operational Risk', 'Technology Risk', 'Data Privacy Risk']
      },
      {
        name: 'Process Execution Error',
        description: 'Risk of manual errors or system failures during process execution',
        category: 'Operational Risk',
        riskLevel: 'MEDIUM' as const,
        taxonomy: ['Operational Risk', 'Process Risk']
      }
    ];

    return baseRisks.map((risk, index) => ({
      id: `ai-risk-${Date.now()}-${index}`,
      code: `RISK-CO-${String(index + 1).padStart(3, '0')}`,
      name: risk.name,
      description: risk.description,
      category: risk.category,
      riskLevel: risk.riskLevel,
      taxonomy: risk.taxonomy,
      confidence: 0.85,
      reasoning: `AI identified this risk based on process step "${processStep.name}" analysis`
    }));
  }

  private generateMockControlSuggestions(
    processStep: ProcessStepContext,
    risks: any[]
  ): AIControlSuggestion[] {
    const baseControls = [
      {
        name: 'Automated Identity Verification System',
        description: 'Real-time identity verification using third-party services and document authentication',
        type: 'Automated/Preventive',
        frequency: 'Per Transaction',
        owner: '',
        regulatory: [
          {
            framework: 'Bank Secrecy Act',
            reference: '31 CFR § 1020.220(a)(2)(i)',
            description: 'Customer identification program requirements'
          },
          {
            framework: 'USA PATRIOT Act',
            reference: 'Section 326',
            description: 'Minimum standards for customer identification'
          }
        ]
      },
      {
        name: 'PEP and Sanctions Screening',
        description: 'Screening against politically exposed persons and sanctions lists',
        type: 'Automated/Detective',
        frequency: 'As Needed',
        owner: '',
        regulatory: [
          {
            framework: 'FinCEN Guidance',
            reference: 'FIN-2018-G001',
            description: 'Customer due diligence requirements'
          }
        ]
      },
      {
        name: 'Enhanced Due Diligence Review',
        description: 'Manual review process for high-risk customers and transactions',
        type: 'Manual/Detective',
        frequency: 'As Needed',
        owner: '',
        regulatory: [
          {
            framework: 'FFIEC BSA/AML Manual',
            reference: 'Section II.A.2.c',
            description: 'Enhanced due diligence procedures'
          }
        ]
      }
    ];

    return baseControls.map((control, index) => ({
      id: `ai-control-${Date.now()}-${index}`,
      code: `CTRL-${String(index + 1).padStart(3, '0')}`,
      name: control.name,
      description: control.description,
      type: control.type,
      frequency: control.frequency,
      owner: '',
      effectiveness: undefined,
      regulatory: control.regulatory,
      confidence: 0.88,
      reasoning: `AI suggested this control to mitigate identified risks in "${processStep.name}"`
    }));
  }

  // Generate realistic control codes
  generateControlCode(processStepId: number, index: number): string {
    const codes = ['CTRL', 'CTL', 'CON'];
    const randomCode = codes[Math.floor(Math.random() * codes.length)];
    return `${randomCode}-${String(processStepId).padStart(2, '0')}${String(index + 1)}`;
  }

  // Generate realistic risk codes  
  generateRiskCode(processStepId: number, index: number): string {
    const codes = ['RISK-CO', 'RSK', 'R'];
    const randomCode = codes[Math.floor(Math.random() * codes.length)];
    return `${randomCode}-${String(index + 1).padStart(3, '0')}`;
  }
}

export default new AIRiskControlService();